/**
 * Safe wrapper around localStorage that never throws.
 *
 * Browsers throw on localStorage access in several situations that are common
 * in this app: private/incognito mode, disabled storage, and — most relevant
 * here — QuotaExceededError when a large report (with embedded base64 images)
 * exceeds the ~5-10 MB per-origin limit. An unguarded setItem in those cases
 * crashes the whole app. These helpers degrade gracefully instead.
 */

const memoryFallback = new Map<string, string>();

export const safeGetItem = (key: string): string | null => {
    try {
        const value = localStorage.getItem(key);
        if (value !== null) return value;
    } catch (e) {
        console.warn(`safeStorage: read failed for "${key}"`, e);
    }
    return memoryFallback.has(key) ? memoryFallback.get(key)! : null;
};

/**
 * Persists a value. Returns true if it reached localStorage, false if it had
 * to fall back to in-memory storage (e.g. quota exceeded). Never throws.
 */
export const safeSetItem = (key: string, value: string): boolean => {
    try {
        localStorage.setItem(key, value);
        memoryFallback.delete(key);
        return true;
    } catch (e) {
        console.warn(`safeStorage: write failed for "${key}", using memory fallback`, e);
        memoryFallback.set(key, value);
        return false;
    }
};

/**
 * Convenience helper for JSON values. Returns `fallback` if the key is missing
 * or the stored value is not valid JSON.
 */
export const safeGetJSON = <T>(key: string, fallback: T): T => {
    const raw = safeGetItem(key);
    if (raw === null) return fallback;
    try {
        return JSON.parse(raw) as T;
    } catch (e) {
        console.warn(`safeStorage: invalid JSON for "${key}"`, e);
        return fallback;
    }
};

export const safeSetJSON = (key: string, value: unknown): boolean => {
    try {
        return safeSetItem(key, JSON.stringify(value));
    } catch (e) {
        console.warn(`safeStorage: stringify failed for "${key}"`, e);
        return false;
    }
};
